import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Hospital, Search, Share2, AlertTriangle, Clock } from "lucide-react";

export default function NhsConnect() {
  const { user } = useAuth();
  const [nhs, setNhs] = useState("");
  const [lookup, setLookup] = useState(null);
  const [summary, setSummary] = useState("");
  const [trustSlug, setTrustSlug] = useState("");
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/trusts/mine").then((r) => {
      if (r.data?.admin_trust) setTrustSlug(r.data.admin_trust.slug);
      else if (r.data?.membership) setTrustSlug(r.data.membership.slug);
    }).catch(() => {});
    api.get("/nhs/share-log").then((r) => setLog(r.data || [])).catch(() => {});
  }, []);

  const doLookup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/nhs/patient-lookup", { nhs_number: nhs });
      setLookup(data);
      toast.success("Mock lookup returned");
    } catch (e) { toast.error(e.response?.data?.detail || "Lookup failed"); }
    finally { setLoading(false); }
  };

  const doShare = async () => {
    if (!summary.trim()) return toast.error("Add a summary first");
    if (!trustSlug) return toast.error("You need to belong to a Trust");
    setLoading(true);
    try {
      await api.post("/nhs/share-safety-plan", { trust_slug: trustSlug, nhs_number: nhs, summary });
      toast.success("Queued (mock) — would post to GP record");
      const r = await api.get("/nhs/share-log");
      setLog(r.data || []);
      setSummary("");
    } catch (e) { toast.error(e.response?.data?.detail || "Share failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="nhs-connect-page">
      <header>
        <div className="flex items-center gap-2">
          <Hospital className="w-6 h-6 text-teal-700" />
          <h1 className="font-display text-3xl font-bold text-slate-900">NHS / GP Connect</h1>
        </div>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Look up an NHS patient demographic record and share an agreed safety-plan summary into the patient's GP record.
          This is a clearly-labelled scaffold while NHS Digital sandbox credentials are obtained.
        </p>
      </header>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-amber-900 flex items-start gap-2" data-testid="nhs-mocked-banner">
        <AlertTriangle className="w-5 h-5 mt-0.5" />
        <div className="text-sm">
          <strong>MOCKED.</strong> Real PDS FHIR lookup and GP Connect DocumentReference posting require ODS code + ITK accreditation +
          NHS Digital API credentials. Configure <code className="bg-amber-100 px-1 rounded">NHS_API_KEY</code> and{" "}
          <code className="bg-amber-100 px-1 rounded">ODS_CODE</code> in backend/.env to enable live mode.
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3" data-testid="nhs-lookup-card">
        <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Search className="w-4 h-4 text-teal-700" /> 1 · Patient demographic lookup</h2>
        <input
          data-testid="nhs-number-input"
          value={nhs}
          onChange={(e) => setNhs(e.target.value.replace(/[^\d ]/g, ""))}
          placeholder="NHS number (10 digits)"
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-teal-500"
        />
        <button
          data-testid="nhs-lookup-btn"
          disabled={loading || !nhs}
          onClick={doLookup}
          className="bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl px-4 py-2.5 font-semibold"
        >
          {loading ? "Looking up…" : "Look up patient (mock)"}
        </button>
        {lookup && (
          <pre data-testid="nhs-lookup-result" className="bg-slate-50 rounded-xl p-3 text-xs text-slate-700 mt-2 overflow-x-auto">
{JSON.stringify(lookup, null, 2)}
          </pre>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3" data-testid="nhs-share-card">
        <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Share2 className="w-4 h-4 text-teal-700" /> 2 · Share safety-plan summary to GP</h2>
        <p className="text-xs text-slate-500">Only with the patient's explicit consent. Stored as a FHIR DocumentReference against the patient.</p>
        <textarea
          data-testid="nhs-summary-input"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={5}
          placeholder="Agreed safety-plan summary text…"
          className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-500"
        />
        <button
          data-testid="nhs-share-btn"
          disabled={loading || !summary.trim() || !nhs}
          onClick={doShare}
          className="bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl px-4 py-2.5 font-semibold"
        >
          {loading ? "Sending…" : "Queue share to GP (mock)"}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="nhs-log-card">
        <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Clock className="w-4 h-4 text-teal-700" /> Share log</h2>
        {log.length === 0 ? (
          <div className="text-sm text-slate-500 mt-2">No shares yet.</div>
        ) : (
          <ul className="mt-3 space-y-2">
            {log.map((l) => (
              <li key={l.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2 text-sm">
                <span className="font-mono text-slate-700">•••• {l.nhs_number_last4}</span>
                <span className="text-slate-500">{l.summary_length} chars</span>
                <span className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString("en-GB")}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">{l.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
